if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: ".m-product-option--dropdown-select",
        };

        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
        this.isStyle2 = false;

        this.onSheetKeydown = this.onSheetKeydown.bind(this);
        this.handleSheetClick = this.handleSheetClick.bind(this);
        this.onSheetVariantPickerChange = this.onSheetVariantPickerChange.bind(this);
      }

      connectedCallback() {
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm ? this.mainProductForm.querySelector(this.selectors.buyNowBtn) : null;
        this.disableSelectedVariantDefault = this.mainProduct?.dataset.disableSelectedVariantDefault === "true" || false;
        this.domNodes = queryDomNodes(this.selectors, this.container);
        this.isStyle2 = this.container?.dataset.stickyAtcLayout === "style_2";

        this.init();
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.sheet) {
          this.sheet.removeEventListener("click", this.handleSheetClick);
        }

        document.removeEventListener("keydown", this.onSheetKeydown);
      }

      init() {
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        const headerHeight = MinimogSettings.headerHeight || 66;
        const rootMargin = `${headerHeight}px 0px 0px 0px`;

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio !== 1) {
                  this.container.style.setProperty("--m-translate-y", 0);
                } else {
                  this.container.style.setProperty("--m-translate-y", "100%");
                }
                document.documentElement.classList[entry.intersectionRatio !== 1 ? "add" : "remove"]("stick-atc-show");
              });
            },
            { threshold: 1, rootMargin }
          );
        }

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;
        if (prodTitle) prodTitle.addEventListener("click", () => __scrollToTop());
        if (mainImage) mainImage.addEventListener("click", () => __scrollToTop());

        if (!this.isStyle2) {
          this.handleCustomFields();
        }

        this.setObserveTarget();
        this.initStyle2Sheet();
        this.syncWithMainProductForm();

        const mql = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        mql.onchange = this.setStickyAddToCartHeight.bind(this);
        this.setStickyAddToCartHeight();

        if (this.domNodes.select) {
          this.domNodes.select.addEventListener("change", (e) => this.handleLegacySelectChange(e));
        }
      }

      handleLegacySelectChange(e) {
        const { target } = e;
        const variantPicker = this.mainProduct.querySelector("variant-picker");
        const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;

        this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
        this.currentVariant
          ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          : this.toggleAddButton(true, window.MinimogStrings.unavailable);

        const selectedOption = target.options[target.selectedIndex];
        if (!selectedOption) return;

        const selectedOptionIds = selectedOption.dataset.options.split(",").filter((id) => id);

        if (variantPicker) {
          selectedOptionIds.forEach((optionId) => {
            const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
            if (!input) return;

            const { tagName } = input;
            switch (tagName) {
              case "OPTION": {
                const inputParent = input.parentNode;
                inputParent.value = input.value;
                break;
              }
              case "INPUT":
                input.checked = true;
                break;
            }
          });

          variantPicker.dispatchEvent(new Event("change"));
        }
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      setObserveTarget() {
        if (this.observer && this.mainProductForm) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);

        if (this.sheetForm) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable, this.sheetForm);
        }
      }

      toggleAddButton(disable = true, text, formScope = this.querySelector(".m-product-form")) {
        if (!formScope) return;

        const addButton = formScope.querySelector('[name="add"]');
        const addButtonText = formScope.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton || !addButtonText) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }
      }

      handleDisableSelectedVariantDefault() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector(".m-product-option--dropdown-select");
        if (!pickerFields) {
          if (this.isStyle2) this.setUnavailable();
          return;
        }

        pickerFields.value = "";
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", this.offsetHeight + "px");
        window.MinimogSettings.stickyAddToCartHeight = this.offsetHeight;
      }

      syncVariantInputs(variant) {
        const variantId = variant?.id ? String(variant.id) : "";
        const stickyVariantInput = this.querySelector("[data-sticky-atc-variant-input]") || this.querySelector("[name='id']");

        if (stickyVariantInput) {
          stickyVariantInput.value = variantId;
        }

        if (this.domNodes.select && variantId) {
          this.domNodes.select.value = variantId;
        }

        if (this.sheetVariantInput) {
          this.sheetVariantInput.value = variantId;
        }
      }

      syncWithMainProductForm() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        this.variantChangeUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = this.mainProduct && e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;

          this.currentVariant = e.data.variant;

          if (this.currentVariant) {
            this.syncVariantInputs(this.currentVariant);
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut);
            if (this.sheetForm) {
              this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut, this.sheetForm);
            }
          } else {
            this.syncVariantInputs(null);
            this.setUnavailable();
          }

          this.syncStyle2VariantPicker();
        });
      }

      initStyle2Sheet() {
        if (!this.isStyle2 || !this.mainProduct) return;

        const sectionId = this.mainProduct.dataset.sectionId;
        this.sheet = this.mainProduct.querySelector(`[data-sticky-atc-sheet="${sectionId}"]`);
        if (!this.sheet) return;

        this.sheetContent = this.sheet.querySelector(".m-sticky-addtocart__variant-sheet-content");
        this.sheetCloseButtons = this.sheet.querySelectorAll("[data-sticky-atc-sheet-close]");
        this.sheetForm = this.sheet.querySelector("[data-sticky-atc-sheet-form]");
        this.sheetVariantInput = this.sheet.querySelector("[data-sticky-atc-sheet-variant-input]");
        this.sheetVariantPicker = this.sheet.querySelector("variant-picker");
        this.mainVariantPicker = this.mainProduct.querySelector("variant-picker");
        this.triggerButton = this.querySelector("[data-sticky-atc-open-sheet]");

        if (this.triggerButton) {
          this.triggerButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.openSheet();
          });
        }

        this.sheetCloseButtons.forEach((button) => {
          button.addEventListener("click", () => this.closeSheet());
        });

        this.sheet.addEventListener("click", this.handleSheetClick);
        document.addEventListener("keydown", this.onSheetKeydown);

        if (this.sheetVariantPicker) {
          this.sheetVariantPicker.addEventListener("change", (event) => this.onSheetVariantPickerChange(event));
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
          if (this.sheet.classList.contains("m-open-modal")) {
            this.closeSheet();
          }
        });

        this.syncStyle2VariantPicker();
      }

      refreshStyle2PickerRefs() {
        if (!this.isStyle2 || !this.mainProduct) return;
        this.mainVariantPicker = this.mainProduct.querySelector("variant-picker");
        if (this.sheet) {
          this.sheetVariantPicker = this.sheet.querySelector("variant-picker");
        }
      }

      onSheetVariantPickerChange(event) {
        this.refreshStyle2PickerRefs();
        if (!this.sheetVariantPicker || !this.mainVariantPicker) return;

        const selectedTarget = event?.target;
        if (selectedTarget?.closest(".m-product-option--node__unavailable")) {
          if (this.sheetForm) {
            this.toggleAddButton(true, window.MinimogStrings.soldOut, this.sheetForm);
          }
          if (this.sheetVariantInput) {
            this.sheetVariantInput.value = "";
          }
          const stickyVariantInput = this.querySelector("[data-sticky-atc-variant-input]");
          if (stickyVariantInput) {
            stickyVariantInput.value = "";
          }
        }

        const selectedOptionValues = this.sheetVariantPicker.selectedOptionValues || [];
        if (!selectedOptionValues.length) return;

        this.applyOptionValuesToPicker(this.mainVariantPicker, selectedOptionValues, true);
        this.updatePickerSelectedLabels(this.sheetVariantPicker);
      }

      applyOptionValuesToPicker(picker, optionValueIds, triggerChange = false) {
        if (!picker || !optionValueIds.length) return;

        optionValueIds.forEach((optionId) => {
          const input = picker.querySelector(`[data-option-value-id="${optionId}"]`);
          if (!input) return;

          if (input.tagName === "OPTION") {
            const select = input.parentNode;
            select.value = input.value;
          } else if (input.tagName === "INPUT") {
            input.checked = true;
          }
        });

        if (triggerChange) {
          picker.dispatchEvent(new Event("change"));
        }
      }

      syncStyle2VariantPicker() {
        this.refreshStyle2PickerRefs();
        if (!this.isStyle2 || !this.mainVariantPicker || !this.sheetVariantPicker) return;

        const mainSelectableNodes = this.mainVariantPicker.querySelectorAll("option[data-option-value-id], input[data-option-value-id]");
        mainSelectableNodes.forEach((mainNode) => {
          const optionId = mainNode.dataset.optionValueId;
          if (!optionId) return;

          const sheetNode = this.sheetVariantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
          if (!sheetNode) return;

          // Espelha apenas a marcacao visual: o picker do sheet recalcula a
          // disponibilidade logo abaixo e nenhum controle e bloqueado.
          sheetNode.dataset.unavailable = mainNode.dataset.unavailable || "false";

          if (sheetNode.tagName === "OPTION") {
            sheetNode.selected = mainNode.selected;
          } else if (sheetNode.tagName === "INPUT") {
            sheetNode.checked = mainNode.checked;
          }
        });

        const selectedOptionValues = this.mainVariantPicker.selectedOptionValues || [];
        this.applyOptionValuesToPicker(this.sheetVariantPicker, selectedOptionValues, false);
        this.sheetVariantPicker.updateOptionsAvailability?.();
        this.updatePickerSelectedLabels(this.sheetVariantPicker);
      }

      updatePickerSelectedLabels(picker) {
        if (!picker) return;

        picker.querySelectorAll(".m-product-option__inner").forEach((optionInner) => {
          const selectedLabel = optionInner.querySelector(".option-label--selected");
          if (!selectedLabel) return;

          const select = optionInner.querySelector("select");
          if (select) {
            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption) {
              selectedLabel.textContent = selectedOption.value;
            }
            return;
          }

          const checkedInput = optionInner.querySelector('input[type="radio"]:checked');
          if (checkedInput) {
            selectedLabel.textContent = checkedInput.value;
          }
        });
      }

      openSheet() {
        if (!this.sheet) return;

        this.lastActiveElement = document.activeElement;
        this.refreshStyle2PickerRefs();
        this.syncStyle2VariantPicker();

        this.sheet.style.setProperty("--m-opacity", "1");
        this.sheet.classList.add("m-open-modal");
        this.sheet.setAttribute("aria-hidden", "false");

        document.documentElement.classList.add("prevent-scroll");

        if (this.sheetContent) {
          this.sheetContent.focus();
        }
      }

      closeSheet() {
        if (!this.sheet) return;

        this.sheet.classList.remove("m-open-modal");
        this.sheet.style.setProperty("--m-opacity", "0");
        this.sheet.setAttribute("aria-hidden", "true");

        document.documentElement.classList.remove("prevent-scroll");

        if (this.lastActiveElement) {
          this.lastActiveElement.focus();
        }
      }

      handleSheetClick(event) {
        if (event.target === this.sheet) {
          this.closeSheet();
        }
      }

      onSheetKeydown(event) {
        if (event.key === "Escape" && this.sheet?.classList.contains("m-open-modal")) {
          this.closeSheet();
        }
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        if (hasCustomFieldRequired && addToCart) {
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        }

        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(this.mainProductInfo);
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout.click());
              }
            },
            true
          );
        }

        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          });
        }
      }
    }
  );
}
